import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataLoader } from '@/components/ui/DataLoader';
import { useDrawdownMatrix } from '@/hooks/react-query/useDrawdowns';
import type { DrawdownMatrix, DrawdownMatrixRow } from '@/types/drawdown';
import { DrawdownMatrixSummary } from './DrawdownMatrixSummary';
import { DrawdownMatrixTable } from './DrawdownMatrixTable';
import {
  LEGEND_STATUSES,
  getLegendSwatchClass,
  getStatusLabel,
} from './drawdownMatrixStatus';

interface DrawdownMatrixViewProps {
  fundId: string;
}

function hasOverdueCell(row: DrawdownMatrixRow): boolean {
  return Object.values(row.cells).some(
    (cell) => cell.days_overdue > 0 && cell.amount_due > cell.amount_paid
  );
}

function filterRows(
  rows: DrawdownMatrixRow[],
  search: string,
  outstandingOnly: boolean,
  overdueOnly: boolean
): DrawdownMatrixRow[] {
  const term = search.trim().toLowerCase();

  return rows.filter((row) => {
    if (term && !row.investor_name.toLowerCase().includes(term)) return false;
    if (outstandingOnly && row.outstanding <= 0) return false;
    if (overdueOnly && !hasOverdueCell(row)) return false;
    return true;
  });
}

export function DrawdownMatrixView({ fundId }: DrawdownMatrixViewProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const matrixQuery = useDrawdownMatrix(fundId);
  const drawdownsPath = `/app/funds/${fundId}?tab=transactions&section=drawdowns`;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(drawdownsPath)}
            className="-ml-2 h-7 px-2 text-muted-foreground"
          >
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Back to drawdowns
          </Button>
          <h1 className="mt-1 text-2xl font-bold">Payment matrix</h1>
          <p className="text-sm text-muted-foreground">
            Every investor against every capital call. Amounts are net of
            prepayment; colour is the payment state on the call&apos;s due date.
            Draft calls show early payments but count towards no totals.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => matrixQuery.refetch()}
          disabled={matrixQuery.isFetching}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${matrixQuery.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <DataLoader query={matrixQuery}>
        {(matrix: DrawdownMatrix) => (
          <div className="space-y-4">
            <DrawdownMatrixSummary
              rows={matrix.rows}
              committed={matrix.committed}
              currency={matrix.currency}
              drawdownCount={
                matrix.columns.filter((column) => column.status !== 'draft')
                  .length
              }
            />

            <div className="flex flex-wrap items-center gap-3 rounded-md border p-2">
              <Input
                placeholder="Search investor..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-8 w-56"
              />
              <Button
                variant={outstandingOnly ? 'default' : 'outline'}
                size="sm"
                className="h-8"
                aria-pressed={outstandingOnly}
                onClick={() => setOutstandingOnly(!outstandingOnly)}
              >
                Outstanding only
              </Button>
              <Button
                variant={overdueOnly ? 'default' : 'outline'}
                size="sm"
                className="h-8"
                aria-pressed={overdueOnly}
                onClick={() => setOverdueOnly(!overdueOnly)}
              >
                Overdue only
              </Button>

              <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {LEGEND_STATUSES.map((status) => (
                  <span key={status} className="flex items-center gap-1.5">
                    <span
                      className={`h-2.5 w-2.5 rounded-sm ${getLegendSwatchClass(status)}`}
                    />
                    {getStatusLabel(status)}
                  </span>
                ))}
              </div>
            </div>

            {matrix.columns.length === 0 ? (
              <p className="py-12 text-center text-muted-foreground">
                No capital calls have been created yet.
              </p>
            ) : (
              <DrawdownMatrixTable
                columns={matrix.columns}
                currency={matrix.currency}
                rows={filterRows(
                  matrix.rows,
                  search,
                  outstandingOnly,
                  overdueOnly
                )}
                onSelectDrawdown={(drawdownId) =>
                  navigate(
                    `/app/funds/${fundId}/drawdowns/${drawdownId}/preview`
                  )
                }
                onSelectInvestor={(investorId) =>
                  navigate(`/app/investors/${investorId}`)
                }
              />
            )}
          </div>
        )}
      </DataLoader>
    </div>
  );
}
